// src/utils/dataUtils.js - UPDATED to match backend column names exactly

// Parse different data types for sorting
export const parseValue = (value, type) => {
  if (!value && value !== 0) return 0;
  
  switch (type) {
    case 'number':
      // Handle both string and number inputs
      if (typeof value === 'number') return value;
      return parseInt(value.toString().replace(/[,]/g, '')) || 0;
    case 'percentage':
      // Handle both string and number inputs
      if (typeof value === 'number') return value;
      return parseFloat(value.toString().replace(/[%,]/g, '')) || 0;
    case 'decimal':
      // Handle decimal values like "Bookings per 1k invites"
      if (typeof value === 'number') return value;
      return parseFloat(value.toString().replace(/[,]/g, '')) || 0;
    case 'string':
      return value.toString().toLowerCase();
    default:
      return value;
  }
};

// UPDATED: Color coding for performance metrics using exact backend column names
export const getColorClass = (value, columnKey) => {
  if (!value && value !== 0) return '';
  
  // Handle both string and number inputs
  let numValue;
  if (typeof value === 'number') {
    numValue = value;
  } else {
    numValue = parseFloat(value.toString().replace(/[,%]/g, ''));
  }
  
  if (isNaN(numValue)) return '';

  // FIXED: Use exact backend column names for thresholds
  const thresholds = {
    'Confirmation conversion': { high: 50, medium: 25 },
    'Invite Conversion': { high: 70, medium: 50 },
    'Yes Conversion %': { high: 60, medium: 40 },
    'Maybe Conversion %': { high: 60, medium: 40 },
    'Bookings conversion': { high: 15, medium: 10 },
    'Bookings per 1k invites': { high: 100, medium: 50 },
  };

  const threshold = thresholds[columnKey];
  if (threshold) {
    if (numValue >= threshold.high) return 'text-green-600 font-medium';
    if (numValue >= threshold.medium) return 'text-yellow-600 font-medium';
    return 'text-red-600 font-medium';
  }

  return 'text-gray-900';
};

// Helper function to safely extract numeric value
const safeParseNumber = (value) => {
  if (typeof value === 'number') return value;
  if (!value && value !== 0) return 0;
  
  // If it's a string, remove formatting and parse
  if (typeof value === 'string') {
    return parseInt(value.replace(/[,]/g, '')) || 0;
  }
  
  return parseInt(value) || 0;
};

// Helper function to safely extract percentage value
const safeParsePercentage = (value) => {
  if (typeof value === 'number') return value;
  if (!value && value !== 0) return 0;
  
  // If it's a string, remove formatting and parse
  if (typeof value === 'string') {
    return parseFloat(value.replace(/[%]/g, '')) || 0;
  }
  
  return parseFloat(value) || 0;
};

// UPDATED: Calculate summary metrics using exact backend column names
export const calculateSummaryMetrics = (data) => {
  const rows = data?.rows || [];
  
  if (rows.length === 0) {
    return {
      totalInvites: 0,
      totalResponses: 0,
      totalAttendees: 0,
      totalBookings: 0,
      avgConversion: 0
    };
  }

  // FIXED: Use grand total with exact backend column names
  if (data.grand_total) {
    const gt = data.grand_total;
    return {
      totalInvites: safeParseNumber(gt['Invite sent'] || 0),
      totalResponses: safeParseNumber(gt['Yes Responses'] || 0) + 
                     safeParseNumber(gt['Maybe Responses'] || 0),
      totalAttendees: safeParseNumber(gt['Attendees'] || 0),
      totalBookings: safeParseNumber(gt['Bookings'] || 0),
      avgConversion: safeParsePercentage(gt['Bookings conversion'] || 0)
    };
  }

  // FIXED: Calculate from rows using exact backend column names
  const totals = rows.reduce((acc, row) => {
    acc.invites += safeParseNumber(row['Invite sent'] || 0);
    acc.responses += safeParseNumber(row['Yes Responses'] || 0) + 
                    safeParseNumber(row['Maybe Responses'] || 0);
    acc.attendees += safeParseNumber(row['Attendees'] || 0);
    acc.bookings += safeParseNumber(row['Bookings'] || 0);
    return acc;
  }, { invites: 0, responses: 0, attendees: 0, bookings: 0 });

  return {
    totalInvites: totals.invites,
    totalResponses: totals.responses,
    totalAttendees: totals.attendees,
    totalBookings: totals.bookings,
    avgConversion: totals.attendees > 0 ? (totals.bookings / totals.attendees * 100) : 0
  };
};

// FIXED: Table column definitions using EXACT backend column names
export const tableColumns = [
  {
    key: 'Title',
    label: 'Title',
    type: 'string',
    sortable: true,
    align: 'left'
  },
  {
    key: 'Invite sent',
    label: 'Invite sent',
    type: 'number',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Yes Responses',
    label: 'Yes Responses',
    type: 'number',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Maybe Responses',
    label: 'Maybe Responses',
    type: 'number',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Confirmation conversion',
    label: 'Confirmation conversion',
    type: 'percentage',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Zoom registrations',
    label: 'Zoom registrations',
    type: 'number',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Attendees',
    label: 'Attendees',
    type: 'number',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Invite Conversion',
    label: 'Invite Conversion',
    type: 'percentage',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Yes Conversion %',
    label: 'Yes Conversion %',
    type: 'percentage',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Maybe Conversion %',
    label: 'Maybe Conversion %',
    type: 'percentage',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Bookings',
    label: 'Bookings',
    type: 'number',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Bookings conversion',
    label: 'Bookings conversion',
    type: 'percentage',
    sortable: true,
    align: 'right'
  },
  {
    key: 'Bookings per 1k invites',
    label: 'Bookings per 1k invites',
    type: 'decimal',
    sortable: true,
    align: 'right'
  }
];

// FIXED: Get field value using exact backend column names
export const getFieldValue = (row, fieldKey) => {
  if (!row) return fieldKey === 'Title' ? 'N/A' : '0';
  
  // FIXED: Direct field access using exact backend column names
  const directValue = row[fieldKey];
  if (directValue !== undefined && directValue !== null && directValue !== '') {
    return directValue;
  }
  
  // ENHANCED: Fallback mappings for debugging (should not be needed now)
  const legacyMappings = {
    'Title': ['Title', 'title', 'test_title', 'clean_test_title'],
    'Invite sent': ['Invite sent', 'invite_sent', 'inviteSent'],
    'Yes Responses': ['Yes Responses', 'yes_responses', 'yesResponses'],
    'Maybe Responses': ['Maybe Responses', 'maybe_responses', 'maybeResponses'],
    'Confirmation conversion': ['Confirmation conversion', 'confirmation_conversion', 'confirmationConversion'],
    'Zoom registrations': ['Zoom registrations', 'zoom_registrations', 'zoomRegistrations'],
    'Attendees': ['Attendees', 'attendees'],
    'Invite Conversion': ['Invite Conversion', 'invite_conversion', 'inviteConversion'],
    'Yes Conversion %': ['Yes Conversion %', 'yes_conversion', 'yesConversion'],
    'Maybe Conversion %': ['Maybe Conversion %', 'maybe_conversion', 'maybeConversion'],
    'Bookings': ['Bookings', 'bookings'],
    'Bookings conversion': ['Bookings conversion', 'bookings_conversion', 'bookingsConversion'],
    'Bookings per 1k invites': ['Bookings per 1k invites', 'bookings_per_1k_invites', 'bookingsPer1kInvites']
  };
  
  const possibleKeys = legacyMappings[fieldKey] || [fieldKey];
  
  for (const key of possibleKeys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
      // Log when using fallback mapping for debugging
      if (key !== fieldKey) {
        console.log(`🔧 Using fallback mapping: "${fieldKey}" → "${key}"`);
      }
      return row[key];
    }
  }
  
  // ENHANCED: Better debugging for missing fields
  console.warn(`⚠️ Field "${fieldKey}" not found in row. Available keys:`, Object.keys(row));
  return fieldKey === 'Title' ? 'N/A' : '0';
};

// LEGACY: Helper function for backward compatibility (deprecated)
export const getRowValue = (row, column) => {
  console.warn('⚠️ getRowValue is deprecated, use getFieldValue instead');
  return getFieldValue(row, column.key);
};

// ENHANCED: Format number for display
export const formatNumber = (value) => {
  const num = parseValue(value, 'number');
  return num.toLocaleString();
};

// ENHANCED: Format percentage for display
export const formatPercentage = (value) => {
  const num = parseValue(value, 'percentage');
  return `${num}%`;
};

// ENHANCED: Format decimal for display
export const formatDecimal = (value, decimals = 2) => {
  const num = parseValue(value, 'decimal');
  return num.toFixed(decimals);
};

// UTILITY: Debug function to check column name consistency
export const debugColumnConsistency = (sampleRow) => {
  console.log('🔍 Column Consistency Check:');
  console.log('Expected columns:', tableColumns.map(col => col.key));
  console.log('Actual row keys:', Object.keys(sampleRow || {}));
  
  const missingColumns = tableColumns
    .map(col => col.key)
    .filter(key => !(sampleRow && sampleRow.hasOwnProperty(key)));
  
  if (missingColumns.length > 0) {
    console.warn('❌ Missing columns:', missingColumns);
  } else {
    console.log('✅ All columns found in data');
  }
  
  return missingColumns;
};


export const formatLastUpdated = (timestamp, timezone = 'America/Los_Angeles') => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    const actualDateTime = date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    });

    let relativeTime;
    if (diffInMinutes < 1) {
      relativeTime = 'Just now';
    } else if (diffInMinutes < 60) {
      relativeTime = `${diffInMinutes}m ago`;
    } else {
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) {
        relativeTime = `${diffInHours}h ago`;
      } else {
        return actualDateTime;
      }
    }

    return `${relativeTime} (${actualDateTime})`;
  };